import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { StripePriceLookupKey, User } from '@paragon/contract-library';
import { EnvironmentState } from '@paragon/environment-tools';

export interface LogInDataState {
  readonly loggedInUser: User | null;
  readonly isAutomaticLogInOngoing: boolean;

  readonly subscription: null | 'none' | 'free' | 'trial' | StripePriceLookupKey;
  readonly subscriptionCancelsAtPeriodEnd?: boolean;
  readonly subscriptionCurrentPeriodEnd?: string; // ISO string

  // If set, the user needs to reset their password before continuing.
  readonly needsPasswordChange: boolean;
}

const initialState: LogInDataState = {
  loggedInUser: null,
  isAutomaticLogInOngoing: false,

  subscription: null,

  needsPasswordChange: false,
};

const logInDataSlice = createSlice({
  name: 'logInData',
  initialState,
  reducers: {
    logIn: (state, action: PayloadAction<{ readonly user: User }>) => {
      state.loggedInUser = castDraft(action.payload.user);
    },

    automaticLogInAttemptStarted: (state) => {
      state.isAutomaticLogInOngoing = true;
    },
    automaticLogInAttemptComplete: (state) => {
      state.isAutomaticLogInOngoing = false;
    },

    setSubscription: (
      state,
      action: PayloadAction<{
        subscription: 'none' | 'free' | 'trial' | StripePriceLookupKey;
        cancelAtPeriodEnd?: boolean;
        currentPeriodEnd?: string;
      }>,
    ) => {
      state.subscription = action.payload.subscription;
      state.subscriptionCancelsAtPeriodEnd = action.payload.cancelAtPeriodEnd;
      state.subscriptionCurrentPeriodEnd = action.payload.currentPeriodEnd;
    },

    passwordChangeRequestedByServer: (state) => {
      state.needsPasswordChange = true;
    },
    passwordChangedInResponseToServerRequest: (state) => {
      state.needsPasswordChange = false;
    },
  },
});

export const {
  logIn,
  automaticLogInAttemptStarted,
  automaticLogInAttemptComplete,
  setSubscription,
  passwordChangeRequestedByServer,
  passwordChangedInResponseToServerRequest,
} = logInDataSlice.actions;
export const logInData = logInDataSlice.reducer;

export interface LoginDataSliceState {
  readonly environment: EnvironmentState;
  readonly logInData: LogInDataState;
}
